// ---------------------------------------------------------------------------
// The receipt itself.
//
// ONE COMPONENT, TWO CALLERS, AND THAT IS THE POINT.
//
// The Receipt Designer renders this with a sample sale and the layout being
// edited. The till renders it with a real sale and the saved layout. They are
// the same code path, so the preview is not a drawing OF the receipt - it is
// the receipt, with different data in it. A preview built separately drifts,
// and the day it drifts is the day an owner arranges a layout, prints, and
// gets something else.
//
// Everything on the paper is decided by `config`. There is no hard-coded line:
// if a switch says hide the cashier's name, the cashier's name is not rendered
// at all rather than rendered invisibly, because a thermal printer prints what
// is in the DOM.
//
// The one thing the config CANNOT do is change the money. Amounts arrive as
// strings and are formatted, never recomputed - a layout choice must not be
// able to alter what the customer is charged.
//
// LIRA FIRST, DOLLARS UNDERNEATH. The shop prices in dollars and is PAID in
// lira: the number the customer counts out, argues about and checks against
// the price on the wall is the lira one. It is the big figure on every line
// and on the total; the dollar figure stays, small, because it is what the
// price was set in and the owner reads the receipt too.
//
// The conversion uses `sale.exchangeRateUsdToLbp` - THE RATE THIS SALE WAS
// RUNG UP AT, stored on the sale - never today's. A receipt reprinted after
// the rate moves has to show the arithmetic the customer actually paid, and
// this is the only reason that column exists.
// ---------------------------------------------------------------------------

import clsx from 'clsx';
import { YaghiLogo } from '../common/YaghiLogo';
import { useI18n } from '../../i18n/I18nProvider';
import {
  formatDateTime,
  formatLbp,
  formatUsd,
  formatWeight,
  toDecimal,
  unitLabel,
  usdToLbpDisplay,
} from '../../utils/format';
import { contentWidthMm, type ReceiptWidth } from '../../utils/printPaper';
import { optionLine } from '../../utils/optionLabel';
import type { ReceiptConfig } from '../../types/design';
import type { Sale } from '../../types';

export interface ReceiptShopDetails {
  nameEn: string;
  nameAr: string;
  address: string;
  phone: string;
}

interface Props {
  config: ReceiptConfig;
  sale: Sale;
  shop: ReceiptShopDetails;
  /// Marks the paper as a duplicate. An unmarked reprint is one of the easier
  /// ways to disguise a missing payment, so this is not a config option.
  /// The designer sets this so its preview does not claim the id the printer
  /// stylesheet targets - two elements with `id="receipt-sheet"` on one page
  /// would make printing pick whichever came first.
  forPreview?: boolean;
}

/// Font scale, from the config. Thermal paper is unforgiving: these are the
/// three sizes that stay legible at 203 dpi on both roll widths.
const BODY_TEXT: Record<ReceiptConfig['fontSize'], string> = {
  SMALL: 'text-[10px]',
  MEDIUM: 'text-[11px]',
  LARGE: 'text-[13px]',
};

const LOGO_HEIGHT: Record<ReceiptConfig['logoSize'], number> = {
  SMALL: 30,
  MEDIUM: 44,
  LARGE: 60,
};

const ALIGN: Record<ReceiptConfig['footerAlignment'], string> = {
  LEFT: 'text-left',
  CENTER: 'text-center',
  RIGHT: 'text-right',
};

export function ReceiptSheet({ config, sale, shop, forPreview = false }: Props) {
  const { t, locale } = useI18n();

  const paperWidth = (config.widthMm === 58 ? 58 : 80) as ReceiptWidth;
  const previewMm = contentWidthMm(paperWidth);

  // Which language the PAPER is in, which is not necessarily the language the
  // screen is in. A shop serving both prints both; a shop that has chosen
  // Arabic prints Arabic even while the owner works in English.
  const showEn = config.language === 'EN' || config.language === 'BILINGUAL';
  const showAr = config.language === 'AR' || config.language === 'BILINGUAL';
  const itemName = (nameEn: string, nameAr: string) =>
    config.language === 'AR' ? nameAr : config.language === 'EN' ? nameEn : (locale === 'ar' ? nameAr : nameEn);

  /// The language the PAPER is in, which is not the language of the screen.
  /// The option label follows it for the same reason the product name does:
  /// a receipt set to print Arabic must not say "Size:" in the middle of it.
  const paperLocale: 'ar' | 'en' =
    config.language === 'AR' ? 'ar' : config.language === 'EN' ? 'en' : locale;

  const gap = config.density === 'COMPACT' ? 'mb-0.5' : 'mb-1.5';
  const body = BODY_TEXT[config.fontSize];

  const divider = (thin = false) => <Divider style={config.dividerStyle} thin={thin} />;

  const methodLabel = (method: string) =>
    method === 'CASH'
      ? t.payment.cash
      : method === 'VISA'
        ? t.payment.visa
        : method === 'CREDIT'
          ? t.payment.credit
          : t.payment.whish;

  const hasDelivery = toDecimal(sale.deliveryFeeUsd).greaterThan(0);
  const hasDiscount = toDecimal(sale.discountUsd).greaterThan(0);

  /// A dollar amount as lira, at THIS SALE'S rate. Not today's, and not a
  /// number written here: `usdToLbpDisplay` is the same helper the till uses,
  /// so the paper and the screen can never round differently.
  ///
  /// The FALLBACK, for a sale rung up before the lira figures were recorded.
  /// Everything below prefers the sale's own lira and comes here only when
  /// there is none - which is exactly how those old receipts were printed at
  /// the time, so a reprint matches the paper the customer was handed.
  const lbp = (usd: string) => usdToLbpDisplay(usd, sale.exchangeRateUsdToLbp, 'en');

  /// The lira the SALE recorded, or the old conversion when it recorded none.
  ///
  /// This is the whole fix on paper: a line rung up at 300,000 prints 300,000,
  /// rather than $3.35 multiplied back into 299,825.
  const exactLbp = (recorded: string | null | undefined, usd: string) =>
    recorded ? formatLbp(recorded) : lbp(usd);

  /// One amount, both currencies, lira leading. `strong` is for the figures
  /// that carry the line - the total of a line and the total of the sale.
  const MoneyRow = MoneyRowFactory(exactLbp);

  /// One amount, both currencies, lira leading. Used for a line total, where
  /// the lira figure is what the customer adds up.
  const Money = ({ usd, lbpExact }: { usd: string; lbpExact?: string | null }) => (
    <span className="flex shrink-0 flex-col items-end leading-tight">
      <span className="font-semibold">{exactLbp(lbpExact, usd)}</span>
      <span className="text-[9px] opacity-80">{formatUsd(usd)}</span>
    </span>
  );

  return (
    <div
      id={forPreview ? undefined : 'receipt-sheet'}
      className={clsx(
        'receipt-sheet bg-white p-3 font-mono leading-snug text-black',
        body,
        config.density === 'COMPACT' && 'leading-tight',
      )}
      style={{ width: `${previewMm}mm` }}
      dir="ltr"
    >
      {/* ================= the shop ================= */}
      <div className={ALIGN[config.logoAlignment]}>
        {config.showLogo ? (
          <div
            className={clsx(
              'mb-1.5 flex',
              config.logoAlignment === 'LEFT'
                ? 'justify-start'
                : config.logoAlignment === 'RIGHT'
                  ? 'justify-end'
                  : 'justify-center',
            )}
          >
            <YaghiLogo height={LOGO_HEIGHT[config.logoSize]} forPrint decorative />
          </div>
        ) : null}

        {config.showBusinessNameEn && showEn ? (
          <p className="text-[13px] font-bold uppercase tracking-wide">{shop.nameEn}</p>
        ) : null}
        {config.showBusinessNameAr && showAr ? (
          <p className="text-[12px] font-bold" dir="rtl">
            {shop.nameAr}
          </p>
        ) : null}
        {config.showAddress && shop.address ? (
          <p className="mt-1 text-[10px]">{shop.address}</p>
        ) : null}
        {config.showPhone && shop.phone ? <p className="text-[10px]">{shop.phone}</p> : null}
        {config.headerText ? (
          <p className="mt-1 whitespace-pre-line text-[10px]">{config.headerText}</p>
        ) : null}
      </div>

      {divider()}

      {/* ================= which sale this is ================= */}
      {config.showReceiptNumber ? <Row label={t.orders.receipt} value={sale.receiptNumber} /> : null}
      {/* A delivery's OR reference, printed BESIDE the receipt identity above
          - the number the shop quotes while the account is out. Two labels,
          two meanings; the receipt row is never overloaded with both. */}
      {sale.orderNumber && sale.orderNumber !== sale.receiptNumber ? (
        <Row label={t.delivery.orderRef} value={sale.orderNumber} />
      ) : null}
      {/* An order that came from the menu: its own number, and the customer
          as they gave themselves - name, phone, where to bring it. Frozen on
          the sale's order, so a reprint says what the paper said. A counter
          sale has none of this and prints exactly as before. */}
      {sale.onlineOrder && (config.showOnlineOrderDetails ?? true) ? (
        <div className="my-1 border-y border-dashed border-black py-1" data-testid="receipt-online-order">
          <Row label={t.receipt.onlineSource} value={sale.onlineOrder.orderNumber} />
          <Row label={t.receipt.onlineCustomer} value={sale.onlineOrder.customerName} />
          <Row label={t.receipt.onlinePhone} value={sale.onlineOrder.customerPhone} />
          {sale.onlineOrder.usdChangeFor ? <p className="text-xs">{t.menu.orders.usdChange.replace('{amount}', String(sale.onlineOrder.usdChangeFor))}</p> : null}
          {[sale.onlineOrder.area, sale.onlineOrder.street].filter(Boolean).length > 0 ? (
            <Row label={t.receipt.onlineAddress} value={[sale.onlineOrder.area, sale.onlineOrder.street].filter(Boolean).join(' - ')} />
          ) : null}
          {[sale.onlineOrder.building, sale.onlineOrder.floor, sale.onlineOrder.landmark].filter(Boolean).length > 0 ? (
            <Row
              label={t.receipt.onlineBuilding}
              value={[sale.onlineOrder.building, sale.onlineOrder.floor, sale.onlineOrder.landmark].filter(Boolean).join(' - ')}
            />
          ) : null}
        </div>
      ) : null}
      {config.showDateTime ? (
        <Row label={t.receipt.date} value={formatDateTime(sale.createdAt, 'en')} />
      ) : null}
      {config.showCashierName ? (
        <Row label={t.receipt.cashier} value={sale.cashier.fullName} />
      ) : null}
      {config.showSaleNumber ? (
        <Row label={t.shift.shiftNumber} value={sale.shift.shiftNumber} />
      ) : null}

      {/* The table, for an order eaten in - a compact line, deliberately not
          the kitchen ticket's shouted banner: the customer holding this paper
          is already sitting at the table it names. Pickup and delivery say
          nothing here (delivery already shows its fee row), and a legacy sale
          with no recorded type says nothing rather than guessing. */}
      {sale.fulfilment === 'ON_TABLE' && sale.tableLabel ? (
        <Row label={t.payment.tableLabel} value={sale.tableLabel} />
      ) : null}

      {divider()}

      {/* ================= the lines ================= */}
      <div className="flex justify-between text-[10px] font-bold uppercase">
        <span>{t.receipt.item}</span>
        <span>{t.receipt.total}</span>
      </div>

      {divider(true)}

      {sale.items.map((item, index) => {
        // A COMBO prints as ONE commercial line: the combo's name and its
        // offer price, with the components beneath as information - no price
        // on any of them, because none was charged on its own. The figures
        // are the SALE's own frozen ones; the combo may have changed since.
        if (item.comboGroupKey) {
          const group = sale.items.filter((entry) => entry.comboGroupKey === item.comboGroupKey);
          if (group[0] !== item) return null;
          const groupLbp = group.reduce((sum, entry) => sum.plus(toDecimal(entry.lineTotalLbp ?? '0')), toDecimal(0));
          const groupUsd = group.reduce((sum, entry) => sum.plus(toDecimal(entry.lineTotalUsd)), toDecimal(0));
          return (
            <div key={item.comboGroupKey} className={gap}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold">
                  {itemName(item.comboNameEn ?? '', item.comboNameAr ?? '')}
                  <span className="ms-1 text-[9px] font-normal">({paperLocale === 'ar' ? 'كومبو' : 'Combo'})</span>
                </p>
                <Money usd={groupUsd.toFixed(2)} lbpExact={groupLbp.toFixed(0)} />
              </div>
              {item.comboRegularPriceLbp && config.showUnitPrice ? (
                <p className="text-[9px] line-through opacity-80">{formatLbp(item.comboRegularPriceLbp)}</p>
              ) : null}
              {group.map((entry) => (
                <p key={entry.id} className="ps-2 text-[10px]">
                  {entry.quantityMode === 'WEIGHT_KG'
                    ? formatWeight(entry.quantity)
                    : `${toDecimal(entry.quantity).toFixed(0)} x`}{' '}
                  {itemName(entry.nameEn, entry.nameAr)}
                  {optionLine(
                    { ar: entry.variantLabelAr, en: entry.variantLabelEn },
                    itemName(entry.variantNameEn ?? '', entry.variantNameAr ?? ''),
                    paperLocale,
                  )
                    ? ` - ${optionLine(
                        { ar: entry.variantLabelAr, en: entry.variantLabelEn },
                        itemName(entry.variantNameEn ?? '', entry.variantNameAr ?? ''),
                        paperLocale,
                      )}`
                    : ''}
                </p>
              ))}
            </div>
          );
        }
        void index;
        const discounted =
          item.promotionType && item.promotionType !== 'NONE' && item.promotionType !== 'COMBO_OFFER' && item.basePriceLbp;
        return (
        <div key={item.id} className={gap}>
          <p className="font-semibold">{itemName(item.nameEn, item.nameAr)}</p>

          {/* WHAT was chosen, and what the choice was called - both from the
              SALE's own snapshot, never from the product as it stands today.
              A reprint a year from now says "الحجم: كبير" even if the shop has
              since decided it calls that a Type, and even if the option itself
              has been renamed or withdrawn.

              Its own line, and its own direction. "الحجم: كبير" is one Arabic
              run inside a sheet laid out left-to-right; declaring the run
              stops the colon being pulled across it. */}
          {optionLine(
            { ar: item.variantLabelAr, en: item.variantLabelEn },
            itemName(item.variantNameEn ?? '', item.variantNameAr ?? ''),
            paperLocale,
          ) ? (
            <p dir={paperLocale === 'ar' ? 'rtl' : 'ltr'}>
              {optionLine(
                { ar: item.variantLabelAr, en: item.variantLabelEn },
                itemName(item.variantNameEn ?? '', item.variantNameAr ?? ''),
                paperLocale,
              )}
            </p>
          ) : null}

          {config.showSku && item.sku ? <p className="text-[9px]">{item.sku}</p> : null}

          <div className="flex items-start justify-between gap-2">
            {/* How much, at what price - in lira, because that is the sum the
                customer checks. The dollar unit price follows underneath in
                small type for the owner's benefit. */}
            <span className="flex min-w-0 flex-col leading-tight">
              <span>
                {item.quantityMode === 'CUSTOM_AMOUNT'
                  ? t.units.CUSTOM_AMOUNT
                  : item.quantityMode === 'WEIGHT_KG'
                    ? [
                        config.showWeight ? formatWeight(item.quantity) : null,
                        config.showUnitPrice
                          ? `${exactLbp(item.unitPriceLbp, item.unitPrice)}/${unitLabel(item.sellingUnit, 'en')}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' x ')
                    : [
                        config.showQuantity ? toDecimal(item.quantity).toFixed(0) : null,
                        config.showUnitPrice ? exactLbp(item.unitPriceLbp, item.unitPrice) : null,
                      ]
                        .filter(Boolean)
                        .join(' x ')}
              </span>

              {config.showUnitPrice && item.quantityMode !== 'CUSTOM_AMOUNT' ? (
                <span className="text-[9px] opacity-80">
                  {formatUsd(item.unitPrice)}
                  {item.quantityMode === 'WEIGHT_KG' ? `/${unitLabel(item.sellingUnit, 'en')}` : ''}
                </span>
              ) : null}
            </span>

            <Money usd={item.lineTotalUsd} lbpExact={item.lineTotalLbp} />
          </div>

          {/* The discount, frozen on the line: the base it came off and the
              percentage. Printed from the SALE's row - a reprint next year
              says the same, whatever the catalogue says by then. */}
          {discounted && config.showUnitPrice ? (
            <p className="flex justify-between text-[9px]">
              <span dir={paperLocale === 'ar' ? 'rtl' : 'ltr'}>
                {paperLocale === 'ar' ? 'خصم' : 'Discount'} {toDecimal(item.discountPercent ?? '0').toFixed(0)}%
              </span>
              <span className="numeric" dir="ltr">
                <span className="line-through">{formatLbp(item.basePriceLbp!)}</span>
                {' → '}
                {formatLbp(item.unitPriceLbp ?? '0')}
              </span>
            </p>
          ) : null}

          {config.showItemNotes && item.note ? (
            <p className="ps-2 text-[10px] italic">* {item.note}</p>
          ) : null}
        </div>
        );
      })}

      {divider()}

      {/* ================= the money ================= */}
      {/* A delivery fee is charged on top of the items, so the total on its own
          would not add up from the lines above. The breakdown appears when
          there is anything to break down. */}
      {(config.showSubtotal && (hasDelivery || hasDiscount)) ||
      (config.showDeliveryFee && hasDelivery) ||
      (config.showDiscount && hasDiscount) ? (
        <>
          {config.showSubtotal ? (
            <MoneyRow label={t.pos.subtotal} usd={sale.subtotalUsd} lbpExact={sale.subtotalLbp} />
          ) : null}
          {config.showDiscount && hasDiscount ? (
            <MoneyRow label={t.receipt.discount} usd={sale.discountUsd} lbpExact={sale.discountLbp} negative />
          ) : null}
          {config.showDeliveryFee && hasDelivery ? (
            <MoneyRow label={t.delivery.fee} usd={sale.deliveryFeeUsd} lbpExact={sale.deliveryFeeLbp} />
          ) : null}
          {divider(true)}
        </>
      ) : null}

      {/* THE TOTAL, and the lira figure is the total. `sale.totalLbp` is the
          stored one - not a conversion done here - so the biggest number on
          the paper is the one the sale itself recorded.

          Both switches are still honoured. A shop that turned the lira total
          off gets the dollar one on its own, in the large type, because then
          that IS its total. */}
      {config.showTotalLbp ? (
        <div className="flex items-baseline justify-between text-[15px] font-bold">
          <span>{t.receipt.grandTotal}</span>
          <span>{formatLbp(sale.totalLbp)}</span>
        </div>
      ) : null}

      {config.showTotalUsd ? (
        <div
          className={clsx(
            'flex justify-between',
            config.showTotalLbp ? 'text-[10px] font-semibold opacity-80' : 'text-[15px] font-bold',
          )}
        >
          <span>{config.showTotalLbp ? '' : t.receipt.grandTotal}</span>
          <span>{formatUsd(sale.totalUsd)}</span>
        </div>
      ) : null}

      {/* The rate this sale was rung up at, so the customer can check the
          arithmetic and a later rate change cannot make the receipt look
          wrong.

          TWO BOXES, NOT ONE SENTENCE. An Arabic label and "1 USD = 89,500
          LBP" in a single text node is one bidirectional paragraph, and the
          Unicode algorithm reorders it: the colon and the leading 1 get
          pulled into the Arabic run and the line comes out as
          "1 : الصرف سعر USD = 89500 LBP". Nobody can read that, and no amount
          of invisible marks fixes it reliably.

          Making them separate flex items removes the question entirely -
          each is laid out on its own, in DOM order, and `dir` on each says
          which way its own contents run. The value carries `dir="ltr"` so
          the figures and currency codes stay in arithmetic order whatever
          the label beside them is doing. */}
      {config.showExchangeRate ? (
        <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1 text-[9px]">
          <span dir={locale === 'ar' ? 'rtl' : 'ltr'}>{t.receipt.rate}:</span>
          <span dir="ltr" className="numeric">
            1 USD = {formatLbp(sale.exchangeRateUsdToLbp, 'en')}
          </span>
        </p>
      ) : null}

      {config.showPaymentMethods || config.showChange ? divider(true) : null}

      {config.showPaymentMethods
        ? sale.payments.map((payment) => (
            <div key={payment.id} className="flex justify-between text-[10px]">
              <span>
                {methodLabel(payment.method)}
                {payment.reference ? ` (${payment.reference})` : ''}
              </span>
              <span className="font-semibold">
                {payment.currency === 'USD' ? formatUsd(payment.amount) : formatLbp(payment.amount)}
              </span>
            </div>
          ))
        : null}

      {config.showChange && toDecimal(sale.changeAmount).greaterThan(0) ? (
        <div className="mt-1 flex justify-between text-[11px] font-bold">
          <span>{t.payment.change}</span>
          <span>
            {sale.changeCurrency === 'USD'
              ? formatUsd(sale.changeAmount)
              : formatLbp(sale.changeAmount)}
          </span>
        </div>
      ) : null}

      {sale.note ? (
        <>
          {divider(true)}
          <p className="text-[10px] italic">{sale.note}</p>
        </>
      ) : null}

      {divider()}

      {/* ================= the bottom ================= */}
      <div className={ALIGN[config.footerAlignment]}>
        {config.thankYouText ? (
          <p className="whitespace-pre-line text-[11px] font-semibold">{config.thankYouText}</p>
        ) : null}
        {config.footerText ? (
          <p className="mt-1 whitespace-pre-line text-[10px]">{config.footerText}</p>
        ) : null}
        {config.showReceiptBarcode ? (
          <p className="mt-1.5 text-[9px] tracking-[0.3em]">{sale.receiptNumber}</p>
        ) : null}
        {config.showQrCode ? (
          // A square of the receipt number, rendered as text rather than as a
          // scannable code. There is no QR encoder in this bundle, and drawing
          // a fake one would put a pattern on the paper that no phone reads.
          <p className="mt-1 text-[9px]">{sale.receiptNumber}</p>
        ) : null}
      </div>
    </div>
  );
}

function Divider({ style, thin }: { style: ReceiptConfig['dividerStyle']; thin: boolean }) {
  if (style === 'NONE') return <div className={thin ? 'my-1' : 'my-2'} />;

  return (
    <div
      className={clsx(
        thin ? 'my-1 border-t' : 'my-2 border-t',
        'border-black',
        style === 'SOLID' ? 'border-solid' : style === 'DOTTED' ? 'border-dotted' : 'border-dashed',
      )}
    />
  );
}

/// A named amount, lira over dollars. The receipt's own `Money` block cannot
/// be used here because this one carries a label on the other side.
function MoneyRowFactory(exactLbp: (recorded: string | null | undefined, usd: string) => string) {
  return function MoneyRow({
    label,
    usd,
    /// The lira the SALE recorded for this figure, when it recorded any.
    lbpExact,
    negative = false,
  }: {
    label: string;
    usd: string;
    lbpExact?: string | null;
    negative?: boolean;
  }) {
    const sign = negative ? '- ' : '';
    return (
      <div className="flex items-start justify-between text-[10px]">
        <span>{label}</span>
        <span className="flex flex-col items-end leading-tight">
          <span className="font-semibold">
            {sign}
            {exactLbp(lbpExact, usd)}
          </span>
          <span className="text-[9px] opacity-80">
            {sign}
            {formatUsd(usd)}
          </span>
        </span>
      </div>
    );
  };
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-[10px]">
      <span>{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
